import { useEffect, useState } from "react";

import useCustomerSubscriptionMutations from "features/subscriptions/datahooks/useCustomerSubscriptionMutations";
import useWorkspaceSubscriptionMutations from "features/subscriptions/datahooks/useWorkspaceSubscriptionMutations";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import { formatDate } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type {
  AgencySubscription,
  RentedSubscription,
  Subscription,
} from "common/types";

interface RemoveChangeButtonProps {
  type: "cancellation" | "change";
  subscription: Subscription | RentedSubscription | AgencySubscription;
  isInitiallyDisabled: boolean;
}

const CANCELLATION_LABELS: Record<
  | Subscription["type"]
  | RentedSubscription["type"]
  | AgencySubscription["type"],
  string
> = {
  user: "Remove cancellation",
  rental: "Keep my order",
  agency: "Remove cancellation",
};

export default function RemoveChangeButton({
  type,
  subscription,
  isInitiallyDisabled,
}: RemoveChangeButtonProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [isDisabled, setIsDisabled] = useState(isInitiallyDisabled);

  const { removeChange, isRemovingChange } =
    useWorkspaceSubscriptionMutations();

  const { removeAgencyScheduledChanges, isRemovingAgencyScheduledChanges } =
    useCustomerSubscriptionMutations();

  const onConfirm = () =>
    subscription.type === "agency"
      ? removeAgencyScheduledChanges()
      : removeChange({ subscription });

  const isConfirming =
    subscription.type === "agency"
      ? isRemovingAgencyScheduledChanges
      : isRemovingChange;

  const label =
    type === "change"
      ? "Remove change"
      : CANCELLATION_LABELS[subscription.type];

  useEffect(() => {
    if (isDisabled) {
      const timeoutId = setTimeout(() => setIsDisabled(false), 10 * 1000);
      return () => clearTimeout(timeoutId);
    }
  }, []);

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <Component>
      <Trigger asChild>
        <Button size={isDesktop ? "sm" : "md"} disabled={isDisabled}>
          {label}
        </Button>
      </Trigger>

      <Content>
        <Header
          title={<>Remove scheduled {type}?</>}
          subtitle={
            type === "change"
              ? `Are you sure you want to remove your scheduled change for ${formatDate(subscription.next_billing * 1000)}?`
              : `Are you sure you want to remove your scheduled cancellation? Your subscription will become active and renew normally on ${formatDate(subscription.cancelled_at * 1000)}`
          }
        />

        <Footer className="justify-end">
          <Close asChild>
            <Button variant="secondary-black" disabled={isConfirming}>
              Cancel
            </Button>
          </Close>

          <Button isLoading={isConfirming} onClick={onConfirm}>
            {label}
          </Button>
        </Footer>
      </Content>
    </Component>
  );
}
